import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Plus, Loader2, ArrowLeft, Building2, ExternalLink, Store,
  Brain, CalendarDays, FolderOpen, FileText, Link2,
  TrendingUp, UserCog, AlertTriangle, X, Trash2, Euro, CalendarClock,
  UserPlus, Percent,
} from "lucide-react";

const STATUS = [
  { k: "active", label: "Attivo", color: "#10B981" },
  { k: "paused", label: "In pausa", color: "#F59E0B" },
  { k: "churned", label: "Perso", color: "#EF4444" },
];
const STATUS_BY = Object.fromEntries(STATUS.map((s) => [s.k, s]));

export default function AgencyClients() {
  const { id } = useParams();
  if (id) return <ClientDetail clientId={id} />;
  return <ClientsList />;
}

// ---------------------------------------------------------------------------
// LISTA CLIENTI
// ---------------------------------------------------------------------------
function ClientsList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/agency-clients")
      .then((r) => setClients(r.data || []))
      .catch(() => toast.error("Errore caricamento"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => filter === "all" ? clients : clients.filter((c) => c.status === filter),
    [clients, filter],
  );
  const counts = useMemo(() => {
    const c = { all: clients.length, active: 0, paused: 0, churned: 0 };
    for (const cl of clients) c[cl.status] = (c[cl.status] || 0) + 1;
    return c;
  }, [clients]);

  return (
    <div data-testid="agency-clients-page" className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight flex items-center gap-3">
            <Store className="h-9 w-9 text-[color:var(--sp-brand)]" /> Clienti
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">
            Ogni cliente è un salone che seguiamo: scheda, Brand Brain, consulenze, task e KPI dal gestionale Memento.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-client-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--sp-brand)] text-white font-bold hover:opacity-90">
          <Plus className="h-4 w-4" /> Nuovo cliente
        </button>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="client-status-tabs">
        <StatusChip active={filter === "all"} onClick={() => setFilter("all")} label="Tutti" count={counts.all} color="#64748B" testId="client-tab-all" />
        {STATUS.map((s) => (
          <StatusChip key={s.k} active={filter === s.k} onClick={() => setFilter(s.k)}
            label={s.label} count={counts[s.k] || 0} color={s.color} testId={`client-tab-${s.k}`} />
        ))}
      </div>

      {loading ? (
        <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sp-brand)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="sp-card p-10 text-center">
          <Store className="h-10 w-10 text-[color:var(--sp-text-muted)] mx-auto mb-3" />
          <p className="text-[color:var(--sp-text-secondary)] mb-4">Nessun cliente. Crea la prima scheda cliente collegando un salone Memento.</p>
          <button onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--sp-brand)] text-white font-bold hover:opacity-90">
            <Plus className="h-4 w-4" /> Nuovo cliente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="clients-grid">
          {filtered.map((c) => {
            const st = STATUS_BY[c.status] || STATUS_BY.active;
            const pct = c.task_total > 0 ? Math.round((c.task_done / c.task_total) * 100) : 0;
            return (
              <Link key={c.id} to={`/admin/clienti/${c.id}`} data-testid={`client-card-${c.id}`}
                className="sp-card p-4 hover:shadow-md transition-shadow border-t-4" style={{ borderTopColor: c.color || "#06B6D4" }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-black text-lg truncate">{c.name}</h3>
                    {c.owner_name && <p className="text-xs text-[color:var(--sp-text-muted)] truncate">{c.owner_name}</p>}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ background: st.color }}>{st.label}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {c.package && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[color:var(--sp-subtle)]">{c.package}</span>}
                  {c.needs_salon_link ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[color:var(--sp-danger)]"><AlertTriangle className="h-3 w-3" /> Salone da collegare</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[color:var(--sp-brand)]"><Building2 className="h-3 w-3" /> Memento collegato</span>
                  )}
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] mb-1">
                    <span>Task</span><span>{pct}% · {c.task_done}/{c.task_total}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: c.color || "#06B6D4" }} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showNew && <NewClientModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function StatusChip({ active, onClick, label, count, color, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${active ? "text-white" : "bg-white border border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:border-[color:var(--sp-brand)]"}`}
      style={active ? { background: color } : undefined}>
      {!active && <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />}
      {label}
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${active ? "bg-white/25" : "bg-[color:var(--sp-subtle)]"}`}>{count}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// NUOVO CLIENTE — collega un salone Memento
// ---------------------------------------------------------------------------
function NewClientModal({ onClose, onSaved }) {
  const [salons, setSalons] = useState([]);
  const [salonId, setSalonId] = useState("");
  const [name, setName] = useState("");
  const [pkg, setPkg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/salons-lookup").then((r) => setSalons(r.data?.salons || [])).catch(() => {});
  }, []);

  const pickSalon = (sid) => {
    setSalonId(sid);
    const s = salons.find((x) => x.id === sid);
    if (s && !name) setName(s.name);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!salonId) { toast.error("Seleziona un salone Memento"); return; }
    if (!name.trim()) { toast.error("Nome obbligatorio"); return; }
    setBusy(true);
    try {
      await api.post("/admin/agency-clients", { salon_tenant_id: salonId, name: name.trim(), package: pkg });
      toast.success("Cliente creato");
      onSaved?.();
    } catch { toast.error("Errore creazione"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-client-form" className="sp-card p-6 w-full max-w-lg space-y-3">
        <h3 className="font-display text-xl font-black">Nuovo cliente</h3>
        <label className="block"><span className="text-xs font-bold block mb-1">Salone Memento *</span>
          <select value={salonId} onChange={(e) => pickSalon(e.target.value)} data-testid="new-client-salon" className="sp-input">
            <option value="">Seleziona salone…</option>
            {salons.map((s) => <option key={s.id} value={s.id} disabled={s.already_linked}>{s.name}{s.already_linked ? " (già collegato)" : ""}</option>)}
          </select>
          <span className="text-[10px] text-[color:var(--sp-text-muted)] mt-1 block">Il cliente è sempre collegato a un salone su Memento (per KPI live e portale).</span>
        </label>
        <label className="block"><span className="text-xs font-bold block mb-1">Nome cliente *</span>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-client-name" className="sp-input" placeholder="Es. Nuovaidea I Parrucchieri" /></label>
        <label className="block"><span className="text-xs font-bold block mb-1">Pacchetto</span>
          <input value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid="new-client-package" className="sp-input" placeholder="Es. Circolo Audax / Reset" /></label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="new-client-save" className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
            {busy ? "Creo…" : "Crea cliente"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DETTAGLIO CLIENTE — scheda a tab
// ---------------------------------------------------------------------------
const TABS = [
  { k: "overview", label: "Panoramica", Icon: Building2 },
  { k: "brain", label: "Brand Brain", Icon: Brain },
  { k: "coaching", label: "Coaching / Consulenze", Icon: CalendarDays },
  { k: "content", label: "Delivery / Contenuti", Icon: FolderOpen },
  { k: "files", label: "File & Dati", Icon: FileText },
];

function ClientDetail({ clientId }) {
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/agency-clients/${clientId}`),
      api.get("/admin/team-members"),
    ]).then(([c, m]) => {
      setClient(c.data);
      setMembers(m.data?.members || []);
    }).catch(() => toast.error("Errore caricamento")).finally(() => setLoading(false));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (changes) => {
    setClient((prev) => ({ ...prev, ...changes }));
    try { await api.patch(`/admin/agency-clients/${clientId}`, changes); }
    catch { toast.error("Errore salvataggio"); }
  }, [clientId]);

  const remove = async () => {
    if (!window.confirm(`Eliminare il cliente "${client.name}"? Le task collegate resteranno senza cliente.`)) return;
    await api.delete(`/admin/agency-clients/${clientId}`);
    toast.success("Cliente eliminato");
    nav("/admin/clienti");
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sp-brand)]" /></div>;
  if (!client) return (
    <div className="sp-card p-10 text-center">
      <p className="mb-3">Cliente non trovato.</p>
      <button onClick={() => nav("/admin/clienti")} className="text-[color:var(--sp-brand)] font-bold underline">Torna ai clienti</button>
    </div>
  );

  const st = STATUS_BY[client.status] || STATUS_BY.active;
  return (
    <div data-testid="client-detail-page" className="space-y-6">
      <button onClick={() => nav("/admin/clienti")} className="text-xs font-bold text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)] inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Torna ai clienti
      </button>

      <div className="sp-card p-5 border-l-8" style={{ borderLeftColor: client.color || "#06B6D4" }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} onBlur={(e) => patch({ name: e.target.value })}
                data-testid="client-name-input" className="font-display text-3xl font-black bg-transparent outline-none border-b-2 border-transparent focus:border-[color:var(--sp-brand)]" />
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full text-white" style={{ background: st.color }}>{st.label}</span>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {client.package && <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[color:var(--sp-subtle)]">{client.package}</span>}
              {client.needs_salon_link ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--sp-danger)]"><AlertTriangle className="h-3.5 w-3.5" /> Salone Memento da collegare</span>
              ) : client.salon ? (
                <Link to={`/admin/tenants/${client.salon_tenant_id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--sp-brand)] hover:underline">
                  <Building2 className="h-3.5 w-3.5" /> {client.salon.name} <ExternalLink className="h-3 w-3" />
                </Link>
              ) : null}
            </div>
          </div>
          <button onClick={remove} data-testid="client-delete-btn" className="p-2 rounded-full text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)] hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-[color:var(--sp-border)]" data-testid="client-tabs">
        {TABS.map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`client-tab-${k}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition ${tab === k ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-transparent text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text-secondary)]"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab client={client} members={members} patch={patch} onLink={load} />}
      {tab === "brain" && <BrainTab client={client} patch={patch} />}
      {tab === "coaching" && <CoachingTab client={client} members={members} patch={patch} />}
      {tab === "content" && <ContentTab client={client} patch={patch} />}
      {tab === "files" && <FilesTab client={client} patch={patch} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TAB: PANORAMICA (anagrafica + team + KPI live)
// ---------------------------------------------------------------------------
function OverviewTab({ client, members, patch, onLink }) {
  const [kpi, setKpi] = useState(null);
  const [salons, setSalons] = useState([]);
  useEffect(() => {
    api.get(`/admin/agency-clients/${client.id}/memento-kpi`).then((r) => setKpi(r.data)).catch(() => setKpi({ linked: false }));
    if (client.needs_salon_link) api.get("/admin/salons-lookup").then((r) => setSalons(r.data?.salons || [])).catch(() => {});
  }, [client.id, client.needs_salon_link]);

  const linkSalon = async (sid) => { await patch({ salon_tenant_id: sid }); onLink?.(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {client.needs_salon_link && (
          <div className="sp-card p-4 border border-[color:var(--sp-danger)]/40 bg-red-50/50">
            <div className="flex items-center gap-2 mb-2 text-[color:var(--sp-danger)] font-bold text-sm"><AlertTriangle className="h-4 w-4" /> Collega il salone Memento</div>
            <p className="text-xs text-[color:var(--sp-text-secondary)] mb-2">Questo cliente non è ancora collegato a un salone Memento: non vedrai i KPI live finché non lo colleghi.</p>
            <select onChange={(e) => e.target.value && linkSalon(e.target.value)} data-testid="link-salon-select" className="sp-input">
              <option value="">Seleziona salone…</option>
              {salons.map((s) => <option key={s.id} value={s.id} disabled={s.already_linked}>{s.name}{s.already_linked ? " (già collegato)" : ""}</option>)}
            </select>
          </div>
        )}
        <div className="sp-card p-4 space-y-3" data-testid="client-anagrafica">
          <h3 className="font-black text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)]">Anagrafica</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Titolare" value={client.owner_name} onSave={(v) => patch({ owner_name: v })} testId="f-owner" />
            <Field label="Email" value={client.email} onSave={(v) => patch({ email: v })} testId="f-email" />
            <Field label="Telefono" value={client.phone} onSave={(v) => patch({ phone: v })} testId="f-phone" />
            <Field label="Città" value={client.city} onSave={(v) => patch({ city: v })} testId="f-city" />
            <div className="sm:col-span-2"><Field label="Indirizzo" value={client.address} onSave={(v) => patch({ address: v })} testId="f-address" /></div>
            <Field label="Pacchetto" value={client.package} onSave={(v) => patch({ package: v })} testId="f-package" />
            <label className="block"><span className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] block mb-1">Stato</span>
              <select value={client.status} onChange={(e) => patch({ status: e.target.value })} data-testid="f-status" className="sp-input !py-1.5 text-sm">
                {STATUS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
              </select></label>
          </div>
        </div>
        <div className="sp-card p-4 space-y-3" data-testid="client-team">
          <h3 className="font-black text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)] flex items-center gap-2"><UserCog className="h-4 w-4" /> Team assegnato</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MemberSelect label="Project Manager" value={client.pm_id} members={members} onChange={(v) => patch({ pm_id: v })} testId="f-pm" />
            <MemberSelect label="Social Media Manager" value={client.smm_id} members={members} onChange={(v) => patch({ smm_id: v })} testId="f-smm" />
            <MemberSelect label="Coach" value={client.coach_id} members={members} onChange={(v) => patch({ coach_id: v })} testId="f-coach" />
            <MemberSelect label="Receptionist" value={client.receptionist_id} members={members} onChange={(v) => patch({ receptionist_id: v })} testId="f-recept" />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="sp-card p-4" data-testid="client-kpi">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)] flex items-center gap-2"><TrendingUp className="h-4 w-4" /> KPI Memento</h3>
            {client.salon_tenant_id && (
              <Link to={`/admin/tenants/${client.salon_tenant_id}`} data-testid="open-portal-btn" className="inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--sp-brand)] hover:underline">
                Apri portale <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
          {!kpi ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[color:var(--sp-brand)]" /></div>
          ) : !kpi.linked ? (
            <p className="text-xs text-[color:var(--sp-text-muted)] py-4 text-center">Collega un salone Memento per vedere i dati live.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Kpi icon={Euro} label="Fatturato mese" value={`€${kpi.revenue_month}`} color="#10B981" />
              <Kpi icon={CalendarClock} label="Appunt. mese" value={kpi.appts_month} color="#06B6D4" />
              <Kpi icon={UserPlus} label="Nuovi clienti" value={kpi.new_clients_month} color="#8B5CF6" />
              <Kpi icon={Percent} label="No-show 30g" value={`${kpi.no_show_rate}%`} color="#EF4444" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl p-3" style={{ background: `${color}12` }}>
      <Icon className="h-4 w-4 mb-1" style={{ color }} />
      <div className="text-lg font-black leading-none">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] mt-1">{label}</div>
    </div>
  );
}

function Field({ label, value, onSave, testId }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] block mb-1">{label}</span>
      <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (value || "") && onSave(v)}
        data-testid={testId} className="sp-input !py-1.5 text-sm w-full" />
    </label>
  );
}

function MemberSelect({ label, value, members, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] block mb-1">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} className="sp-input !py-1.5 text-sm w-full">
        <option value="">— Nessuno</option>
        {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// TAB: BRAND BRAIN (blocchi markdown liberi)
// ---------------------------------------------------------------------------
function BrainTab({ client, patch }) {
  const blocks = [
    { k: "positioning", label: "Posizionamento & angolo differenziante", ph: "Come si posiziona il salone, cosa lo rende unico…" },
    { k: "target", label: "Target ideale", ph: "Demografica, area, reddito, stile di vita…" },
    { k: "offer", label: "Offerta front-end & servizi core", ph: "Lead magnet, servizi core, beneficio principale, prezzi…" },
    { k: "buyer_persona", label: "Buyer persona — paure & desideri", ph: "Personaggi verbatim con paure e desideri profondi…" },
    { k: "emotional_diary", label: "Diario emotivo", ph: "Scenario di frustrazione e di raggiungimento…" },
    { k: "tone_of_voice", label: "Tono di voce & lessico del brand", ph: "Pilastri del tono, parole da usare/evitare, metafore signature…" },
    { k: "copy_notes", label: "Note operative per il copy", ph: "Come aprire i contenuti, offerta da spingere, differenziatori…" },
  ];
  return (
    <div className="space-y-4" data-testid="client-brain">
      <div className="sp-card p-4 flex items-start gap-3 bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20">
        <Brain className="h-5 w-5 text-[color:var(--sp-brand)] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-[color:var(--sp-text-secondary)]">Il <strong>Brand Brain</strong> del cliente: la fonte da cui delivery e coaching estraggono copy, post e sponsorizzate coerenti. Puoi incollare qui i blocchi del tuo company brain.</p>
      </div>
      <MarkdownField label="Brief / chi è il cliente" value={client.brief} ph="Anagrafica sintetica, dove sta ora, cosa vuole, priorità attive…" onSave={(v) => patch({ brief: v })} testId="brain-brief" />
      {blocks.map((b) => (
        <MarkdownField key={b.k} label={b.label} value={client[b.k]} ph={b.ph} onSave={(v) => patch({ [b.k]: v })} testId={`brain-${b.k}`} />
      ))}
    </div>
  );
}

function MarkdownField({ label, value, ph, onSave, testId }) {
  const [v, setV] = useState(value || "");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setV(value || ""); setDirty(false); }, [value]);
  return (
    <div className="sp-card p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-black text-sm">{label}</h3>
        {dirty && <span className="text-[10px] font-bold text-[color:var(--sp-text-muted)]">Modifiche non salvate…</span>}
      </div>
      <textarea value={v} placeholder={ph} data-testid={testId}
        onChange={(e) => { setV(e.target.value); setDirty(true); }}
        onBlur={() => { if (dirty) { onSave(v); setDirty(false); } }}
        rows={Math.max(4, Math.min(16, v.split("\n").length + 1))}
        className="sp-input text-sm w-full font-mono leading-relaxed" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TAB: COACHING / CONSULENZE
// ---------------------------------------------------------------------------
function CoachingTab({ client, members, patch }) {
  const consultations = client.consultations || [];
  const [form, setForm] = useState({ title: "", date: "", url: "", notes: "", coach_id: "" });
  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    const next = [...consultations, { ...form, coach_id: form.coach_id || null }];
    await patch({ consultations: next });
    setForm({ title: "", date: "", url: "", notes: "", coach_id: "" });
  };
  const del = async (id) => { await patch({ consultations: consultations.filter((c) => c.id !== id) }); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="client-coaching">
      <form onSubmit={add} className="sp-card p-4 space-y-3 h-fit">
        <h3 className="font-black text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)]">Nuova consulenza</h3>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titolo consulenza" data-testid="cons-title" className="sp-input !py-1.5 text-sm" />
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cons-date" className="sp-input !py-1.5 text-sm" />
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Link registrazione (Drive, Zoom…)" data-testid="cons-url" className="sp-input !py-1.5 text-sm" />
        <select value={form.coach_id} onChange={(e) => setForm({ ...form, coach_id: e.target.value })} data-testid="cons-coach" className="sp-input !py-1.5 text-sm">
          <option value="">Coach…</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
        </select>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note della call" rows={3} data-testid="cons-notes" className="sp-input text-sm" />
        <button type="submit" data-testid="cons-add" className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90"><Plus className="h-4 w-4" /> Aggiungi</button>
      </form>
      <div className="lg:col-span-2 space-y-2">
        {consultations.length === 0 ? (
          <div className="sp-card p-8 text-center text-[color:var(--sp-text-muted)] text-sm">Nessuna consulenza registrata.</div>
        ) : [...consultations].reverse().map((c) => (
          <div key={c.id} className="sp-card p-4" data-testid={`cons-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-sm">{c.title}</div>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-[color:var(--sp-text-muted)] flex-wrap">
                  {c.date && <span>{new Date(c.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>}
                  {c.coach_id && memberById[c.coach_id] && <span className="font-bold">· {memberById[c.coach_id].name || memberById[c.coach_id].email}</span>}
                  {c.url && <a href={c.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[color:var(--sp-brand)] font-bold hover:underline"><Link2 className="h-3 w-3" /> Registrazione</a>}
                </div>
                {c.notes && <p className="text-xs text-[color:var(--sp-text-secondary)] mt-2 whitespace-pre-wrap">{c.notes}</p>}
              </div>
              <button onClick={() => del(c.id)} data-testid={`cons-del-${c.id}`} className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><X className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TAB: DELIVERY / CONTENUTI (storico + link)
// ---------------------------------------------------------------------------
function ContentTab({ client, patch }) {
  return (
    <div className="space-y-4" data-testid="client-content">
      <div className="sp-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Cartella Drive contenuti" value={client.drive_url} onSave={(v) => patch({ drive_url: v })} testId="c-drive" />
        <Field label="Google Sheet numeri" value={client.sheet_url} onSave={(v) => patch({ sheet_url: v })} testId="c-sheet" />
      </div>
      <MarkdownField label="Storico contenuti (campagne, articoli, ads, email, post)" value={client.content_history}
        ph="Traccia qui campagne, articoli rivista, ads, email, post/script prodotti…" onSave={(v) => patch({ content_history: v })} testId="content-history" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TAB: FILE & DATI
// ---------------------------------------------------------------------------
function FilesTab({ client, patch }) {
  return (
    <div className="space-y-4" data-testid="client-files">
      <div className="sp-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a href={client.drive_url || "#"} target={client.drive_url ? "_blank" : undefined} rel="noreferrer"
          className={`rounded-xl p-4 flex items-center gap-3 ${client.drive_url ? "bg-[color:var(--sp-brand)]/5 hover:bg-[color:var(--sp-brand)]/10" : "bg-[color:var(--sp-subtle)] opacity-60 pointer-events-none"}`}>
          <FolderOpen className="h-6 w-6 text-[color:var(--sp-brand)]" />
          <div><div className="font-bold text-sm">Cartella Drive</div><div className="text-[11px] text-[color:var(--sp-text-muted)]">{client.drive_url ? "Apri contenuti" : "Nessun link (vai su Contenuti)"}</div></div>
        </a>
        <a href={client.sheet_url || "#"} target={client.sheet_url ? "_blank" : undefined} rel="noreferrer"
          className={`rounded-xl p-4 flex items-center gap-3 ${client.sheet_url ? "bg-[color:var(--sp-brand)]/5 hover:bg-[color:var(--sp-brand)]/10" : "bg-[color:var(--sp-subtle)] opacity-60 pointer-events-none"}`}>
          <FileText className="h-6 w-6 text-[color:var(--sp-brand)]" />
          <div><div className="font-bold text-sm">Google Sheet numeri</div><div className="text-[11px] text-[color:var(--sp-text-muted)]">{client.sheet_url ? "Apri foglio" : "Nessun link (vai su Contenuti)"}</div></div>
        </a>
      </div>
      <MarkdownField label="Note & dati vari" value={client.files_notes} ph="Appunti, credenziali, dati amministrativi…" onSave={(v) => patch({ files_notes: v })} testId="files-notes" />
    </div>
  );
}
